import { normalizeMobileNumber } from 'helpers/mobileNumber';
import {
	createRecordSuggestionIndex, RecordSuggestion, SuggestionField, SuggestionResults, SuggestionSnapshot,
} from 'helpers/recordSuggestions';
import './recordAutocomplete.css';

declare global {
	interface JQuery<TElement = HTMLElement> {
		typeahead(options: object, dataset: object): JQuery<TElement>;
		typeahead(command: 'open' | 'close' | 'destroy'): JQuery<TElement>;
		typeahead(command: 'val'): string;
		typeahead(command: 'val', value: string): JQuery<TElement>;
		typeahead(command: 'select', element: HTMLElement): JQuery<TElement>;
	}
}

interface AutocompleteOptions {
	getSnapshot: () => SuggestionSnapshot;
	subscribe?: (listener: () => void) => () => void;
	loadImage?: (image: string) => Promise<string | undefined>;
}

interface Binding {
	input: HTMLInputElement;
	refresh: () => void;
	close: () => void;
	clearContact: () => void;
	dispose: () => void;
	wasConnected: boolean;
}

let nextId = 0;
const NS = '.recordSuggestions';
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">' +
	'<rect width="40" height="40" rx="4" fill="#e5e7eb"/>' +
	'<circle cx="20" cy="14" r="7" fill="#9ca3af"/>' +
	'<path d="M6 38v-5a14 14 0 0 1 28 0v5" fill="#9ca3af"/></svg>'
);

/** Insert text nodes/marks, never HTML derived from a record or from the query. */
function highlightedText(parent: HTMLElement, text: string, query: string) {
	const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
	let start = 0;
	const lower = text.toLowerCase();
	while (start < text.length) {
		let position = text.length;
		let length = 0;
		for (const token of tokens) {
			const found = lower.indexOf(token, start);
			if (found >= 0 && (found < position || (found === position && token.length > length))) {
				position = found;
				length = token.length;
			}
		}
		parent.appendChild(document.createTextNode(text.slice(start, position)));
		if (!length) break;
		const mark = document.createElement('mark');
		mark.textContent = text.slice(position, position + length);
		parent.appendChild(mark);
		start = position + length;
	}
}

export function createRecordAutocomplete(options: AutocompleteOptions) {
	const $ = jQuery;
	const index = createRecordSuggestionIndex();
	const bindings = new Set<Binding>();
	const images = new Map<string, Promise<string | undefined>>();
	let managerDisposed = false;
	let snapshot = options.getSnapshot();

	function sameSnapshot(next: SuggestionSnapshot) {
		return snapshot.history === next.history && snapshot.directory === next.directory && snapshot.session === next.session;
	}

	function prune() {
		for (const binding of Array.from(bindings)) {
			if (binding.input.isConnected) binding.wasConnected = true;
			else if (binding.wasConnected) binding.dispose();
		}
	}

	const observer = new MutationObserver(prune);
	observer.observe(document.body, { childList: true, subtree: true });
	const unsubscribe = options.subscribe?.(() => {
		if (managerDisposed) return;
		const next = options.getSnapshot();
		if (sameSnapshot(next)) return;
		if (snapshot.session !== next.session) images.clear();
		snapshot = next;
		index.clear();
		prune();
		for (const binding of bindings) {
			binding.clearContact();
			binding.refresh();
		}
	});

	function bind(input: HTMLInputElement, field: SuggestionField, form: HTMLElement) {
		// A reused edit dialog must not accumulate plugin roots, listeners or helper text.
		Array.from(bindings).find((binding) => binding.input === input)?.dispose();
		if (input.disabled || input.readOnly) return;
		const $input = $(input);
		if ($input.hasClass('tt-input')) $input.typeahead('destroy');
		const id = `record-suggestions-${++nextId}`;
		const menu = document.createElement('div');
		menu.className = 'record-suggestions';
		menu.id = id;
		menu.setAttribute('role', 'listbox');
		menu.setAttribute('aria-label', field === 'name' ? 'Name suggestions' : 'Reference suggestions');
		menu.style.display = 'none';
		document.body.appendChild(menu);
		const $menu = $(menu);
		const helper = document.createElement('span');
		helper.className = 'record-suggestion-live';
		helper.id = `${id}-help`;
		helper.setAttribute('role', 'status');
		helper.setAttribute('aria-live', 'polite');
		input.insertAdjacentElement('afterend', helper);
		const contact = document.createElement('div');
		contact.className = 'record-suggestion-contact';
		contact.setAttribute('aria-live', 'polite');
		input.insertAdjacentElement('afterend', contact);
		const previousDescription = input.getAttribute('aria-describedby');
		const previousAutocomplete = input.getAttribute('autocomplete');
		const previousLabel = input.getAttribute('aria-label');
		let composing = false;
		let disposed = false;
		let query = '';
		let result: SuggestionResults = { suggestions: [], total: 0, unavailableSources: [] };
		const phone = form.querySelector<HTMLInputElement>('input[name="phone"]');

		function clearContact() { contact.replaceChildren(); }

		function place() {
			if (disposed || menu.style.display === 'none') return;
			const rect = input.getBoundingClientRect();
			const viewport = window.visualViewport;
			const left = viewport?.offsetLeft ?? 0;
			const top = viewport?.offsetTop ?? 0;
			const right = left + (viewport?.width ?? window.innerWidth);
			const bottom = top + (viewport?.height ?? window.innerHeight);
			if (!input.isConnected || rect.bottom <= top || rect.top >= bottom) {
				$input.typeahead('close');
				return;
			}
			const width = Math.min(Math.max(rect.width, 300), right - left - 16);
			const x = Math.max(left + 8, Math.min(rect.left, right - width - 8));
			const below = bottom - rect.bottom - 8;
			const above = rect.top - top - 8;
			const upwards = below < 180 && above > below;
			menu.style.width = `${width}px`;
			menu.style.left = `${x}px`;
			menu.style.maxHeight = `${Math.max(0, Math.min(360, upwards ? above : below))}px`;
			menu.style.top = upwards ? 'auto' : `${rect.bottom + 4}px`;
			menu.style.bottom = upwards ? `${window.innerHeight - rect.top + 4}px` : 'auto';
			if (below <= 0 && above <= 0) $input.typeahead('close');
		}

		function show() {
			if (composing || input.disabled || input.readOnly || input.value.trim().length < 3) return;
			menu.style.display = 'block';
			input.setAttribute('aria-expanded', 'true');
			place();
		}

		function hide() {
			menu.style.display = 'none';
			input.setAttribute('aria-expanded', 'false');
			input.removeAttribute('aria-activedescendant');
		}

		function message() {
			const sourceNotice = result.unavailableSources.length ?
				`${result.unavailableSources.map((source) => source === 'mjbj' ? 'MjBj directory' : 'Anumati history').join(' and ')} unavailable. ` : '';
			return `${sourceNotice}${result.total > 20 ? `Showing 20 of ${result.total}. Refine your search for more.` :
				result.total ? `${result.total} matching suggestion${result.total === 1 ? '' : 's'}.` :
					'No matches. You can keep the value you typed.'}`;
		}

		function renderSuggestion(item: RecordSuggestion) {
			const root = document.createElement('div');
			root.dataset.suggestionId = item.id;
			const image = document.createElement('img');
			image.className = 'record-suggestion-image';
			image.alt = '';
			image.src = PLACEHOLDER_IMAGE;
			root.appendChild(image);
			const content = document.createElement('div');
			content.className = 'record-suggestion-content';
			root.appendChild(content);
			const primary = document.createElement('div');
			primary.className = 'record-suggestion-name';
			highlightedText(primary, item.value, query);
			content.appendChild(primary);
			const sources = document.createElement('div');
			sources.className = 'record-suggestion-source';
			sources.textContent = item.sources.map((source) => source === 'anumati' ? 'Anumati history' : 'MjBj directory').join(' + ');
			content.appendChild(sources);
			if (item.matchReason) {
				const reason = document.createElement('div');
				reason.className = 'record-suggestion-match';
				highlightedText(reason, item.matchReason, query);
				content.appendChild(reason);
			}
			const secondary = document.createElement('div');
			secondary.className = 'record-suggestion-details';
			const mobile = item.phones.length ?
				`${field === 'reference' ? 'Referrer mobile' : 'Mobile'}: ${item.phones.join(', ')}` :
				field === 'reference' ? 'Referrer mobile unavailable' : 'No mobile available';
			secondary.textContent = [
				mobile,
				item.date ? `Date: ${item.date}` : '',
				item.recordCount > 1 ? `${item.recordCount} records` : '',
			].filter(Boolean).join(' | ');
			content.appendChild(secondary);
			if (item.image && options.loadImage) {
				let pending = images.get(item.image);
				if (!pending) {
					if (images.size >= 50) images.delete(images.keys().next().value);
					pending = options.loadImage(item.image);
					images.set(item.image, pending);
				}
				pending.then((url) => {
					if (!disposed && url && root.isConnected) image.src = url;
				}).catch((error) => {
					console.warn('Could not load suggestion photo', error);
				});
				image.onerror = () => {
					image.onerror = null;
					image.src = PLACEHOLDER_IMAGE;
				};
			}
			return root;
		}

		function select(item: RecordSuggestion) {
			clearContact();
			if (field !== 'name' || !phone || phone.disabled || phone.readOnly || !item.phones.length) return;
			function applyPhone(number: string) {
				if (phone.disabled || phone.readOnly || input.value !== item.value) return;
				$(phone).val(number).trigger('change');
				clearContact();
			}
			if (item.phones.length === 1 && !phone.value.trim()) {
				applyPhone(item.phones[0]);
				return;
			}
			if (item.phones.length === 1 && normalizeMobileNumber(phone.value) === item.phones[0]) return;
			const explanation = document.createElement('span');
			explanation.textContent = item.phones.length > 1 ?
				'Multiple mobiles available. Choose which to use: ' : 'Your entered mobile was kept. ';
			contact.appendChild(explanation);
			item.phones.forEach((number) => {
				const button = document.createElement('button');
				button.type = 'button';
				button.textContent = `Use this mobile: ${number}`;
				button.onclick = () => applyPhone(number);
				contact.appendChild(button);
			});
		}

		// Capture precedes the plugin: Tab must not silently autocomplete and trap focus,
		// and Enter choosing a row must not bubble to a record form's submit handlers.
		function onKey(event: KeyboardEvent) {
			const key = event.key || ({ 9: 'Tab', 13: 'Enter', 27: 'Escape' }[event.keyCode] ?? '');
			if (composing || event.isComposing) {
				event.stopImmediatePropagation();
				return;
			}
			if (key === 'Tab') {
				$input.typeahead('close');
				event.stopImmediatePropagation();
			} else if (key === 'Enter' && menu.style.display !== 'none') {
				event.preventDefault();
				event.stopImmediatePropagation();
				const selected = menu.querySelector<HTMLElement>('.tt-cursor');
				if (selected) $input.typeahead('select', selected);
				else $input.typeahead('close');
			} else if (key === 'Escape') {
				$input.typeahead('close');
				event.preventDefault();
				event.stopImmediatePropagation();
			}
		}
		const onCompositionStart = () => { composing = true; hide(); };
		const onCompositionEnd = () => { composing = false; refresh(); };
		const onInput = (event: Event) => {
			clearContact();
			if (composing) event.stopImmediatePropagation();
			if (input.value.trim().length < 3) hide();
		};
		input.addEventListener('keydown', onKey, true);
		input.addEventListener('input', onInput, true);
		input.addEventListener('compositionstart', onCompositionStart);
		input.addEventListener('compositionend', onCompositionEnd);
		$input.typeahead({
			hint: false, highlight: false, minLength: 3, menu: $menu,
			classNames: { suggestion: 'record-suggestion', menu: 'record-suggestions' },
		}, {
			name: field, limit: 20, display: 'value', async: false,
			source: (value: string, callback: (suggestions: RecordSuggestion[]) => void) => {
				query = value;
				result = index.search(field, value, options.getSnapshot());
				callback(result.suggestions);
			},
			templates: {
				suggestion: renderSuggestion,
				header: () => $('<div class="record-suggestion-summary" role="presentation">').text(message()),
				notFound: () => $('<div class="record-suggestion-summary" role="presentation">').text(message()),
			},
		});
		$input.attr({
			role: 'combobox', 'aria-autocomplete': 'list', 'aria-haspopup': 'listbox',
			'aria-controls': id, 'aria-expanded': 'false',
			'aria-describedby': [previousDescription, helper.id].filter(Boolean).join(' '),
			'aria-label': previousLabel ?? (field === 'name' ? 'Name' : 'Reference'),
		});
		$input.on(`typeahead:beforeselect${NS}`, (event) => {
			if (input.disabled || input.readOnly || composing) event.preventDefault();
		}).on(`typeahead:open${NS}`, () => {
			for (const other of bindings) if (other.input !== input) other.close();
			show();
		}).on(`typeahead:close${NS}`, hide)
			.on(`typeahead:render${NS}`, () => {
				const elements = menu.querySelectorAll<HTMLElement>('.tt-selectable');
				elements.forEach((element, position) => {
					element.id = `${id}-option-${position}`;
					element.setAttribute('role', 'option');
					element.setAttribute('aria-selected', 'false');
				});
				helper.textContent = message();
				if (document.activeElement === input) show();
			})
			.on(`typeahead:cursorchange${NS}`, () => {
				menu.querySelectorAll<HTMLElement>('.tt-selectable').forEach((element) => {
					const active = element.classList.contains('tt-cursor');
					element.setAttribute('aria-selected', String(active));
					if (active) input.setAttribute('aria-activedescendant', element.id);
				});
				if (!menu.querySelector('.tt-cursor')) input.removeAttribute('aria-activedescendant');
			})
			.on(`typeahead:select${NS}`, (_event, item: RecordSuggestion) => select(item))
			.on(`change${NS} record-suggestions:reset${NS}`, clearContact);

		function refresh() {
			if (disposed || document.activeElement !== input || composing || input.disabled || input.readOnly) {
				$input.typeahead('close');
				return;
			}
			const value = input.value;
			const start = input.selectionStart;
			const end = input.selectionEnd;
			$input.typeahead('val', '');
			$input.typeahead('val', value);
			input.setSelectionRange(start, end);
		}

		const onReset = () => {
			clearContact();
			$input.typeahead('close');
			// Native reset applies defaultValue after the reset event.
			window.setTimeout(() => {
				if (!disposed) $input.typeahead('val', input.value);
			}, 0);
		};
		form.addEventListener('reset', onReset);
		window.addEventListener('resize', place);
		window.addEventListener('scroll', place, true);
		window.visualViewport?.addEventListener('resize', place);
		window.visualViewport?.addEventListener('scroll', place);
		const binding: Binding = {
			input, wasConnected: input.isConnected, clearContact, refresh,
			close: () => $input.typeahead('close'),
			dispose() {
				if (disposed) return;
				disposed = true;
				$input.off(NS).typeahead('destroy');
				$menu.off(NS).remove();
				helper.remove();
				contact.remove();
				input.removeEventListener('keydown', onKey, true);
				input.removeEventListener('input', onInput, true);
				input.removeEventListener('compositionstart', onCompositionStart);
				input.removeEventListener('compositionend', onCompositionEnd);
				form.removeEventListener('reset', onReset);
				window.removeEventListener('resize', place);
				window.removeEventListener('scroll', place, true);
				window.visualViewport?.removeEventListener('resize', place);
				window.visualViewport?.removeEventListener('scroll', place);
				['role', 'aria-autocomplete', 'aria-haspopup', 'aria-controls', 'aria-expanded', 'aria-activedescendant'].forEach((attr) => input.removeAttribute(attr));
				if (previousDescription) input.setAttribute('aria-describedby', previousDescription);
				else input.removeAttribute('aria-describedby');
				if (previousLabel === null) input.removeAttribute('aria-label');
				else input.setAttribute('aria-label', previousLabel);
				if (previousAutocomplete === null) input.removeAttribute('autocomplete');
				else input.setAttribute('autocomplete', previousAutocomplete);
				bindings.delete(binding);
			},
		};
		bindings.add(binding);
	}

	return {
		bindForm(form: HTMLElement) {
			if (managerDisposed) return;
			for (const field of ['name', 'reference'] as const) {
				const input = form.querySelector<HTMLInputElement>(`input[name="${field}"]`);
				if (input) bind(input, field, form);
			}
		},
		resetForm(form: HTMLElement) {
			for (const binding of bindings) {
				if (form.contains(binding.input)) {
					binding.clearContact();
					binding.close();
					$(binding.input).typeahead('val', binding.input.value);
				}
			}
		},
		dispose() {
			if (managerDisposed) return;
			managerDisposed = true;
			unsubscribe?.();
			observer.disconnect();
			Array.from(bindings).forEach((binding) => binding.dispose());
			index.clear();
			images.clear();
		},
	};
}
